import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AppointmentsAPI from '../../api/appointments';
import { extractRecord, extractList } from '../utils/appointmentEnvelope';

export const state = {
  records: [],
  metrics: null,
  metricsLoading: false,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isCancelling: false,
    isExporting: false,
    isFetchError: false,
  },
};

export const getters = {
  getAppointments(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAppointmentById: _state => id => {
    return _state.records.find(record => record.id === Number(id)) || {};
  },
};

export const actions = {
  get: async function getAppointments({ commit }, params = {}) {
    commit(types.SET_APPOINTMENT_UI_FLAG, {
      isFetching: true,
      isFetchError: false,
    });
    try {
      const response = await AppointmentsAPI.list(params);
      commit(types.SET_APPOINTMENTS, extractList(response));
    } catch {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isFetchError: true });
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isFetching: false });
    }
  },

  // Audit 2026-05-07 — try/finally (sem catch) para preservar `error.response`
  // intacto até o caller. Antes reescrevia com `throw new Error(error.response.data.message)`,
  // mas `message` não existe no payload do backend (`{errors: {slot: [...]}}`),
  // virava `Error("undefined")`, e o modal caía no fallback GENERIC_ERROR
  // ("Base: Não foi possível criar..."). Bug reportado por Igor Silva.
  create: async function createAppointment({ commit }, appointmentData) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isCreating: true });
    try {
      const response = await AppointmentsAPI.create(appointmentData);
      const record = extractRecord(response.data);
      commit(types.ADD_APPOINTMENT, record);
      return record;
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateAppointment({ commit }, { id, ...updateData }) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.update(id, updateData);
      const record = extractRecord(response.data);
      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
    }
  },

  cancel: async function cancelAppointment({ commit }, { id, reason } = {}) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isCancelling: true });
    try {
      // cancel → DELETE /appointments/:id → 204 No Content
      // Remove from local store; parent will re-fetch to get updated list
      await AppointmentsAPI.cancel(id, { reason });
      commit(types.DELETE_APPOINTMENT, id);
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isCancelling: false });
    }
  },

  confirm: async function confirmAppointment({ commit }, id) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.confirm(id);
      const record = extractRecord(response.data);
      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
    }
  },

  complete: async function completeAppointment({ commit }, id) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.complete(id);
      const record = extractRecord(response.data);
      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
    }
  },

  noShow: async function noShowAppointment({ commit }, id) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AppointmentsAPI.noShow(id);
      const record = extractRecord(response.data);
      commit(types.EDIT_APPOINTMENT, record);
      return record;
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteAppointment({ commit }, id) {
    commit(types.SET_APPOINTMENT_UI_FLAG, { isDeleting: true });
    try {
      await AppointmentsAPI.delete(id);
      commit(types.DELETE_APPOINTMENT, id);
    } finally {
      commit(types.SET_APPOINTMENT_UI_FLAG, { isDeleting: false });
    }
  },

  fetchMetrics: async function fetchAppointmentsMetrics(
    { commit },
    { from, to } = {}
  ) {
    commit(types.SET_APPOINTMENTS_METRICS_LOADING, true);
    try {
      const response = await AppointmentsAPI.metrics({ from, to });
      commit(types.SET_APPOINTMENTS_METRICS, response.data.data);
      return response.data.data;
    } catch (_error) {
      // Ignore error — component shows empty state
      return undefined;
    } finally {
      commit(types.SET_APPOINTMENTS_METRICS_LOADING, false);
    }
  },
};

export const mutations = {
  [types.SET_APPOINTMENT_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_APPOINTMENTS]: MutationHelpers.set,
  [types.ADD_APPOINTMENT]: MutationHelpers.create,
  [types.EDIT_APPOINTMENT]: MutationHelpers.update,
  [types.DELETE_APPOINTMENT]: MutationHelpers.destroy,

  [types.SET_APPOINTMENTS_METRICS](_state, data) {
    _state.metrics = data;
  },
  [types.SET_APPOINTMENTS_METRICS_LOADING](_state, loading) {
    _state.metricsLoading = loading;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
